// Invites (PROTOCOL.md §9.2): the owner makes, lists and revokes them; a new writer brings one to sign-up. A code is
// 12 Crockford characters (60 random bits), shown once; the relay keeps only its SHA-256. The link is
// `APP_ORIGIN/join#<code>`, so the code stays in the fragment.
import { toB64u, utf8 } from '../shared/b64.ts';
import { sha256 } from '../shared/crypto.ts';
import type { Api } from './api.ts';

export const INVITE_LENGTH = 12;
const ALPHABET = /^[0-9A-HJKMNP-TV-Z]{12}$/;

/** A code as typed or pasted (a whole `/join#…` link too): upper case, look-alikes folded, separators dropped; null if it can't be one. */
export function normalInviteCode(text: string): string | null {
  const raw = text.includes('#') ? text.slice(text.lastIndexOf('#') + 1) : text;
  const code = raw.toUpperCase().replace(/[-\s]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  return ALPHABET.test(code) ? code : null;
}

/** `XXXX-XXXX-XXXX`, for showing. */
export const groupInviteCode = (code: string) => (normalInviteCode(code) ?? code).replace(/(.{4})(?=.)/g, '$1-');

/** What the relay keeps of a code: `b64u(SHA-256("writing-suite/invite/v1:" + code))` of the normalised code. */
export async function inviteHash(code: string): Promise<string> {
  const normal = normalInviteCode(code);
  if (!normal) throw new Error('not an invite code');
  return toB64u(await sha256(utf8(`writing-suite/invite/v1:${normal}`)));
}

/** The code in a `/join#<code>` URL (or `location.hash`), if it holds one. */
export function inviteFromLink(url: string): string | null {
  const i = url.indexOf('#');
  return i < 0 ? null : normalInviteCode(url.slice(i + 1));
}

export interface NewInvite {
  id: string;
  /** Shown once: the relay can't show it again. */
  code: string;
  link: string;
  expiresAt: number;
}

export interface Invite {
  id: string;
  note: string | null;
  createdAt: number;
  expiresAt: number;
  usedAt?: number;
  usedByName?: string;
  revokedAt?: number;
}

/** The state an invite is in, for the owner's list. */
export function inviteState(invite: Invite, now = Date.now()): 'used' | 'revoked' | 'expired' | 'open' {
  if (invite.usedAt !== undefined) return 'used';
  if (invite.revokedAt !== undefined) return 'revoked';
  if (invite.expiresAt <= now) return 'expired';
  return 'open';
}

/** (owner) A new invite; `expiresInDays` 1–90, default 14. */
export const createInvite = (api: Api, o: { note?: string; expiresInDays?: number } = {}) => api.call<NewInvite>('POST', '/api/invites', o);

/** (owner) Every invite made, newest first. */
export const listInvites = (api: Api) => api.get<Invite[]>('/api/invites');

/** (owner) Revokes an invite: unused, it can no longer be used. */
export async function revokeInvite(api: Api, id: string): Promise<void> {
  await api.call('DELETE', `/api/invites/${encodeURIComponent(id)}`);
}

/** Whether signing up here needs an invite (false on an empty relay, where the first account becomes the owner). */
export async function inviteRequired(api: Api): Promise<boolean> {
  return (await api.call<{ inviteRequired: boolean }>('GET', '/api/signup/status', undefined, false)).inviteRequired;
}

/** Why a sign-up was refused, from the relay's error code: `inviteRequired`, `inviteInvalid`, or null for anything else. */
export const inviteRefusal = (code: string): 'inviteRequired' | 'inviteInvalid' | null =>
  code === 'inviteRequired' || code === 'inviteInvalid' ? code : null;
